import React from 'react'
import { func } from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useUser } from '@fs/zion-user'
import { trackEvent } from '@fs/zion-analytics'
import { css } from '@emotion/core'
import { ChoiceChips, Button, useStatusOverlay, useContainerWidth } from '@fs/zion-ui'
import ZionForm, { FormTextField, useZionForm } from '@fs/zion-form'
import { useTreeState, useTreeDispatch, ACTIONS as TREE_ACTIONS } from './state/TreeStore'
import { ACTIONS } from '../aam/AppAam/state/AamStore'
import AncestorAvatar from './AncestorAvatar'
import GedcomX, { isMale, isFemale } from '../shared/helpers/gedcom_X'
import {
  determineFormLabelsByGenderType,
  determineAncestorOwnershipLabels,
  determineChildByAtn,
  determineSpouse,
  determineRelationshipLevel,
} from './helpers/ancestor_Determinant'
import isValidForm from './helpers/treeFormValidator'
import PlaceInput from '../shared/components/PlaceInput/src/PlaceInput'
import sendPersonToServer from './helpers/treeEndpointsCaller'
import animations from '../shared/services/cssAnimations'

const inputDecorations = {
  male: '#01b6d1',
  female: '#bd0057',
  unknown: '#aaaaaa',
}

const infoEntryCss = css`
  width: 100%;
  height: 100%;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
`

const inputCss = css`
  width: 70%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  position: relative;
`

const ancestorImageContainer = css`
  position: relative;
  width: 100%;
`

const boyBackground = css`
  background: ${inputDecorations.male};
  width: 100%;
  height: 125px;
`

const girlBackground = css`
  background: ${inputDecorations.female};
  width: 100%;
  height: 125px;
`

const unkBackground = css`
  background: ${inputDecorations.unknown};
  width: 100%;
`

const buildResolverErrors = (errorReport, translate) => {
  const personErrors = Object.entries(errorReport?.person ?? {}).reduce((acc, [fieldName, value]) => {
    acc[fieldName] = {
      type: 'manual',
      message: translate(value.key, value.defaultValue),
    }
    return acc
  }, {})

  if (Object.keys(personErrors).length === 0) {
    return {}
  }

  return { person: personErrors }
}

export default function AncestorEditor({ setIsTreeShowing, aamDispatch }) {
  const user = useUser()
  const atWidth = useContainerWidth()
  const state = useTreeState()
  const treeDispatch = useTreeDispatch()
  const person = state.userTree[state?.anchorATN]
  const child = determineChildByAtn(state, state?.anchorATN)
  const spouse = determineSpouse(state, state?.anchorATN)
  const [t] = useTranslation()
  const gcx = GedcomX()
  const showStatusoverlay = useStatusOverlay()
  const isNotMe = person.atn !== 1

  const btnContainer = css`
    display: flex;
    flex: 1;
    justify-content: center;
    button#save {
      margin: 5px;
    }
    button#tree {
      margin-top: 5px;
      margin-inline-end: 0;
      margin-bottom: 5px;
      margin-inline-start: 5px;
    }
    a#previous {
      margin-inline-end: 10px;
      ${person.atn === 1 && 'visibility: hidden; pointer-events: none'};
    }
    a#next {
      margin-inline-start: 10px;
      ${person.atn === 7 && 'visibility: hidden; pointer-events: none'};
      ${!person.personId && user.signedIn && 'visibility: hidden; pointer-events: none'};
    }
  `

  const textInfoInputCss = css`
    ${atWidth({ default: 'width: 100%;', md: 'width: 700px;' })};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 0;
    margin-inline-end: auto;
    margin-bottom: 20px;
    margin-inline-start: auto;
    border-radius: 0 0 5px 5px;
    padding-top: 30px;
    height: 100%;
  `

  const ancestorTitle = css`
    ${atWidth({ default: 'font-size: 1.6em', md: 'font-size: 2em' })};
    ${atWidth({ default: 'padding: 1.2em', md: 'padding: 1em' })};
    font-weight: bold;
    text-align: center;
    position: relative;
    inset-inline-start: 0;
    inset-inline-end: 0;
    top: -15px;
    height: 228px;
    color: white;
    animation: ${animations('fade-in')} 2s ease;
  `

  const getChipInputCss = (genderType) => css`
    ${atWidth({ default: 'margin-left: 20px', lg: 'margin-left: 250px' })};
    width: 50%;
    padding-top: 50px;
    text-align: center;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    label[active=''] {
      ${isFemale(genderType) && 'background-color: #bd0057'};
      ${isFemale(genderType) && 'color: #fff'};
      ${isFemale(genderType) && 'border: none'};
    }
    label[active='']:hover {
      ${isFemale(genderType) && 'background-color: hsla(333, 100%, 35%, 1)'};
    }
  `

  const getLivingChoiceCss = (genderType) => css`
    label[active=''] {
      ${isFemale(genderType) && 'background-color: #bd0057'};
      ${isFemale(genderType) && 'color: #fff'};
      ${isFemale(genderType) && 'border: none'};
    }
    label[active='']:hover {
      ${isFemale(genderType) && 'background-color: hsla(333, 100%, 35%, 1)'};
    }
  `

  const submitCss = css`
    ${atWidth({ default: 'width: 100%;', md: 'width: 600px;' })};
    display: flex;
    margin-top: 0;
    margin-inline-end: auto;
    margin-bottom: 25px;
    margin-inline-start: auto;
    flex-direction: column;
  `

  const determineAppropriateHighlight = (genderType) => {
    if (isMale(genderType)) {
      return boyBackground
    }
    if (isFemale(genderType)) {
      return girlBackground
    }

    return unkBackground
  }

  const resolver = React.useCallback(
    async (values) => {
      const errorReport = isValidForm(values, state.includeLocation, state.atfGuestOptionSelected)
      const errors = buildResolverErrors(errorReport, t)

      return {
        values,
        errors,
      }
    },
    [state.includeLocation, state.atfGuestOptionSelected, t]
  )

  const methods = useZionForm({
    mode: 'all',
    defaultValues: { person },
    resolver,
  })

  const {
    watch,
    register,
    reset,
    setValue,
    formState: { isValid, isSubmitting },
  } = methods

  const watchedPerson = watch('person', person)
  const currentGender = watchedPerson?.genderType
  const livingChipSelected = watchedPerson?.living

  const handleSetGender = (selectedChip) => {
    const newGenderType = selectedChip.value
    setValue('person.genderType', newGenderType, { shouldDirty: true, shouldValidate: true })
  }

  const handleSetLivingState = (selectedChip) => {
    const isLiving = selectedChip.value === 'true' || selectedChip.value === true
    setValue('person.living', isLiving, { shouldDirty: true, shouldValidate: true })

    if (isLiving) {
      setValue('person.dod', null, { shouldDirty: true, shouldValidate: true })
      setValue('person.pod', null, { shouldDirty: true, shouldValidate: true })
      setValue('person.podId', null, { shouldDirty: true, shouldValidate: true })
    }
  }

  const addOrUpdatePersonOnServer = async ({ payloadDetails, onFinishedFn }) => {
    try {
      if (user?.signedIn && user?.personId) {
        await sendPersonToServer({ payloadDetails })
      }
      onFinishedFn()
    } catch (err) {
      console.error(err)
      showStatusoverlay({
        message: 'An error has occurred',
        type: 'error',
        dismissable: true,
      })
    }
  }

  const handleSubmit = async (values) => {
    const payloadDetails = {
      person: values.person,
      child,
      spouse: spouse?.personId ?? spouse,
      t,
    }

    const onFinishedFn = () => {
      reset({ person: values.person })
      treeDispatch({
        type: TREE_ACTIONS.COMMIT_ANCESTOR,
        payload: {
          person: values.person,
        },
      })
      showStatusoverlay({
        message: t('atf.ancestor-editor.validation.success', 'Changes saved successfully'),
        type: 'success',
        dismissable: true,
      })
      setIsTreeShowing(true)
    }

    if (!user.signedIn) {
      values.person.displayName = `${values.person.name} ${values.person.surname}`
      aamDispatch({
        type: ACTIONS.ADD_GUEST_ANCESTOR,
        data: values.person,
      })
    }

    if (user.signedIn && !child?.personId && values.person.atn !== 1) {
      showStatusoverlay({
        message: t(
          'atf.ancestor-editor.validation.missing-child',
          'Record not saved, ensure you add children before parents.'
        ),
        type: 'error',
        dismissable: true,
      })
    } else {
      addOrUpdatePersonOnServer({ payloadDetails, onFinishedFn })
    }
  }

  React.useEffect(() => {
    register('person.genderType')
    register('person.living')
    register('person.pob')
    register('person.pobId')
    register('person.pod')
    register('person.podId')
  }, [register])

  React.useEffect(() => {
    reset({ person })
  }, [person, reset])

  const labels = determineFormLabelsByGenderType(t, currentGender)
  const parentDetails = determineAncestorOwnershipLabels(person, child, currentGender)

  return (
    <ZionForm methods={methods} onSubmit={handleSubmit}>
      <div css={infoEntryCss}>
        <div css={determineAppropriateHighlight(currentGender)}>
          <div css={ancestorTitle}>{t(parentDetails.key, parentDetails.defaultValue)}</div>
        </div>
        <div css={ancestorImageContainer}>
          <AncestorAvatar
            fieldName="person.portraitThumbnailLink"
            fieldValue={watchedPerson?.portraitThumbnailLink}
            genderType={currentGender}
          />
        </div>
        <div css={textInfoInputCss}>
          <div css={getChipInputCss(currentGender)}>
            <ChoiceChips
              name="person.genderType"
              onSelectionChange={handleSetGender}
              chips={[
                { label: t('atf.ancestor-editor.male', 'Male'), value: gcx.MALE, selected: isMale(currentGender) },
                {
                  label: t('atf.ancestor-editor.female', 'Female'),
                  value: gcx.FEMALE,
                  selected: isFemale(currentGender),
                },
              ]}
            />
          </div>
          <div css={inputCss}>
            <FormTextField
              data-testid="ancestor-editor-personname-text-field"
              name="person.name"
              type="text"
              placeholder={labels?.placeholderFirstName}
              label={t('atf.ancestor-editor.labels.first-name', 'First Name')}
            />
          </div>

          <div css={inputCss}>
            <FormTextField
              data-testid="ancestor-editor-personsurname-text-field"
              name="person.surname"
              type="text"
              placeholder={labels?.placeholderSurname}
              label={labels?.labelSurname}
            />
          </div>

          <div css={inputCss}>
            <FormTextField
              data-testid="ancestor-editor-persondob-text-field"
              type="number"
              name="person.dob"
              label={t('atf.ancestor-editor.labels.vitality-date-birth', '4-digit birth year')}
              placeholder={t('atf.ancestor-editor.placeholders.example-date', 'ex: 1960')}
            />
          </div>

          {state.includeLocation && (
            <div css={inputCss}>
              <PlaceInput
                fieldName="person.pob"
                idFieldName="person.pobId"
                fieldValue={watchedPerson?.pob}
                idFieldValue={watchedPerson?.pobId ? `${watchedPerson?.pobId}` : null}
                label={t('atf.ancestor-editor.labels.vitality-place-birth', 'Place of birth')}
                onOptionSelected={(el) => {
                  setValue('person.pob', el?.primary ?? '', { shouldDirty: true, shouldValidate: true })
                  setValue('person.pobId', el?.placeId ?? '', { shouldDirty: true, shouldValidate: true })
                }}
              />
            </div>
          )}

          {isNotMe && !livingChipSelected && (
            <div css={inputCss}>
              <FormTextField
                data-testid="ancestor-editor-persondod-text-field"
                type="number"
                name="person.dod"
                label={t('atf.ancestor-editor.labels.vitality-date-death', '4-digit death year')}
                placeholder={t('atf.ancestor-editor.placeholders.example-date', 'ex: 1960')}
              />
            </div>
          )}

          {isNotMe && !livingChipSelected && (
            <div css={inputCss}>
              <PlaceInput
                fieldName="person.pod"
                idFieldName="person.podId"
                fieldValue={watchedPerson?.pod}
                idFieldValue={watchedPerson?.podId ? `${watchedPerson?.podId}` : null}
                label={t('atf.ancestor-editor.labels.vitality-place-death', 'Place of death')}
                onOptionSelected={(el) => {
                  setValue('person.pod', el?.primary ?? '', { shouldDirty: true, shouldValidate: true })
                  setValue('person.podId', el?.placeId ?? '', { shouldDirty: true, shouldValidate: true })
                }}
              />
            </div>
          )}

          <div css={getLivingChoiceCss(currentGender)}>
            <ChoiceChips
              name="person.living"
              onSelectionChange={handleSetLivingState}
              chips={[
                {
                  label: t('atf.ancestor-editor.living', 'Living'),
                  value: 'true',
                  selected: livingChipSelected,
                },
                {
                  label: t('atf.ancestor-editor.deceased', 'Deceased'),
                  value: 'false',
                  selected: !livingChipSelected,
                  disabled: !isNotMe,
                },
              ]}
            />
          </div>

          <div css={submitCss}>
            <div css={btnContainer}>
              <Button
                data-testid="ancestor-editor-save-button"
                id="save"
                disabled={!isValid || isSubmitting}
                emphasis="high"
                type="submit"
                onClick={() =>
                  trackEvent({
                    event_name: `Add Tree Flow - saved ${determineRelationshipLevel(person.atn)}`,
                  })
                }
              >
                {t('atf.ancestor-editor.save', 'Save')}
              </Button>
            </div>
          </div>
          <div css={btnContainer}>
            <Button
              data-testid="ancestor-editor-go-back-to-your-family-tree-button"
              id="tree"
              emphasis="low"
              onClick={() => setIsTreeShowing(true)}
            >
              {t('atf.ancestor-editor.cancel', 'Go Back To Your Family Tree')}
            </Button>
          </div>
        </div>
      </div>
    </ZionForm>
  )
}

AncestorEditor.propTypes = {
  setIsTreeShowing: func,
  aamDispatch: func,
}
